import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ClipboardList, Plus, Trash2, GripVertical } from "lucide-react";

const FIELD_TYPES = [
  { value: "text", label: "Texte" }, { value: "email", label: "Email" },
  { value: "phone", label: "Telephone" }, { value: "select", label: "Liste deroulante" },
  { value: "checkbox", label: "Cases a cocher" }, { value: "textarea", label: "Zone de texte" },
  { value: "date", label: "Date" }, { value: "number", label: "Nombre" },
];

export function FormFieldsTab({ customFields, addField, removeField, updateField, moveField, fieldsSaving, saveFields }) {
  return (
    <div className="space-y-6" data-testid="form-tab">
      <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2"><ClipboardList className="h-4 w-4 text-[#0099CC]" /> Champs personnalises</h3>
            <p className="text-[#64748b] text-xs mt-1">Ajoutez des champs au formulaire d'inscription (en plus du nom, email, telephone).</p>
          </div>
          <Button data-testid="add-field-btn" onClick={addField} size="sm" className="bg-[#0099CC] hover:bg-[#0088B5] text-white">
            <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter
          </Button>
        </div>

        {customFields.length === 0 ? (
          <div className="text-center py-8">
            <ClipboardList className="h-10 w-10 text-[#2A3441] mx-auto mb-3" />
            <p className="text-[#64748b]">Aucun champ personnalise</p>
            <p className="text-[#4A5568] text-xs mt-1">Les champs par defaut (nom, email, telephone) sont toujours inclus.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {customFields.map((f, idx) => (
              <div key={f.id} className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-4" data-testid={`field-${idx}`}>
                <div className="flex items-start gap-3">
                  <div className="flex flex-col gap-1 pt-1">
                    <button onClick={() => moveField(idx, -1)} disabled={idx === 0} className="text-[#64748b] hover:text-white disabled:opacity-20 text-xs">&#9650;</button>
                    <GripVertical className="h-4 w-4 text-[#4A5568]" />
                    <button onClick={() => moveField(idx, 1)} disabled={idx === customFields.length - 1} className="text-[#64748b] hover:text-white disabled:opacity-20 text-xs">&#9660;</button>
                  </div>
                  <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="col-span-2 sm:col-span-1">
                      <label className="text-[10px] text-[#64748b] mb-1 block">Libelle</label>
                      <Input value={f.label} onChange={e => updateField(f.id, "label", e.target.value)} placeholder="Ex: Societe" className="bg-[#0B0F19] border-[#2A3441] text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] text-[#64748b] mb-1 block">Type</label>
                      <select value={f.type} onChange={e => updateField(f.id, "type", e.target.value)} className="w-full bg-[#0B0F19] border border-[#2A3441] text-white rounded-lg px-3 py-2 text-sm">
                        {FIELD_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-[#64748b] mb-1 block">Placeholder</label>
                      <Input value={f.placeholder || ""} onChange={e => updateField(f.id, "placeholder", e.target.value)} className="bg-[#0B0F19] border-[#2A3441] text-white text-sm" />
                    </div>
                    <div className="flex items-end gap-2">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={f.required} onChange={e => updateField(f.id, "required", e.target.checked)} className="accent-[#0099CC]" />
                        <span className="text-xs text-[#94A3B8]">Requis</span>
                      </label>
                      <button onClick={() => removeField(f.id)} className="text-red-400 hover:text-red-300 p-1.5 rounded transition-colors"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
                {(f.type === "select" || f.type === "checkbox") && (
                  <div className="mt-2 ml-8">
                    <label className="text-[10px] text-[#64748b] mb-1 block">Options (separees par des virgules)</label>
                    <Input value={(f.options || []).join(", ")} onChange={e => updateField(f.id, "options", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder="Option 1, Option 2, Option 3" className="bg-[#0B0F19] border-[#2A3441] text-white text-xs" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {customFields.length > 0 && (
          <div className="flex justify-end mt-4">
            <Button data-testid="save-fields-btn" onClick={saveFields} disabled={fieldsSaving} className="bg-[#0099CC] hover:bg-[#0088B5] text-white">
              {fieldsSaving ? "Enregistrement..." : "Enregistrer le formulaire"}
            </Button>
          </div>
        )}
      </div>

      {/* Preview */}
      {customFields.length > 0 && (
        <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4 text-sm">Apercu du formulaire</h3>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div className="opacity-50">
              <label className="text-[#64748b] text-xs mb-1 block">Prenom *</label>
              <div className="bg-[#151B2B] border border-[#2A3441] rounded-lg px-3 py-2 text-[#4A5568]">Jean</div>
            </div>
            <div className="opacity-50">
              <label className="text-[#64748b] text-xs mb-1 block">Nom *</label>
              <div className="bg-[#151B2B] border border-[#2A3441] rounded-lg px-3 py-2 text-[#4A5568]">Dupont</div>
            </div>
            <div className="opacity-50">
              <label className="text-[#64748b] text-xs mb-1 block">Email *</label>
              <div className="bg-[#151B2B] border border-[#2A3441] rounded-lg px-3 py-2 text-[#4A5568]">jean@example.com</div>
            </div>
            {customFields.map(f => (
              <div key={f.id}>
                <label className="text-[#94A3B8] text-xs mb-1 block">{f.label || "..."} {f.required && "*"}</label>
                {f.type === "textarea" ? (
                  <div className="bg-[#151B2B] border border-[#0099CC]/30 rounded-lg px-3 py-2 text-[#4A5568] h-16">{f.placeholder || ""}</div>
                ) : f.type === "select" ? (
                  <div className="bg-[#151B2B] border border-[#0099CC]/30 rounded-lg px-3 py-2 text-[#4A5568]">{(f.options || [])[0] || "Selectionnez..."}</div>
                ) : f.type === "checkbox" ? (
                  <div className="flex flex-wrap gap-2">{(f.options || []).map(o => <span key={o} className="bg-[#151B2B] border border-[#0099CC]/30 rounded-lg px-2 py-1 text-[#4A5568] text-xs">{o}</span>)}</div>
                ) : (
                  <div className="bg-[#151B2B] border border-[#0099CC]/30 rounded-lg px-3 py-2 text-[#4A5568]">{f.placeholder || ""}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
